import { useEffect } from 'react';
import { useReducedMotion } from 'framer-motion';

import { cn } from '@rocco/utils/cn';

import { ISLAND_GLASS_LIGHT } from '../styles';
import type { IslandMode } from '../types';

type PaginationMode = Extract<IslandMode, { kind: 'pagination' }>;

const PlayIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
);

const PauseIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path d="M6 5h4v14H6zM14 5h4v14h-4z" fill="currentColor" />
    </svg>
);

const ReplayIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path
            d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"
            fill="currentColor"
        />
    </svg>
);

/**
 * Apple-faithful gallery pagination (1:1 with the AirPods-Max control) — two
 * separate light-glass elements 56px / 3.5rem tall: a circular play/pause button
 * (toggles the carousel autoplay) and a dots pill whose active dot is elongated.
 */
/** Cap the visible dots — products can have dozens of slides, Apple never does. */
const MAX_DOTS = 7;

export const PaginationIslandContent = ({ mode }: { mode: PaginationMode }) => {
    const {
        total,
        activeIndex,
        onSelect,
        isPlaying,
        onTogglePlay,
        onAdvance,
        onReplay,
        atEnd: reachedEnd,
        onVisible,
        label,
        autoplayDelayMs,
    } = mode;

    // The owner reports when autoplay has stopped on the last slide (computed
    // from the live snap count, so it can't disagree with the stop logic); the
    // leading control then becomes a replay button that restarts from slide one.
    const atEnd = !!onReplay && !!reachedEnd;

    // Reduced motion: no autoplay at all — no moving progress fill, no timed
    // slide changes, no play/pause button. The active dot is a static bar and
    // the user navigates by tapping dots (WCAG 2.2.2 / prefers-reduced-motion).
    const reduce = useReducedMotion() ?? false;

    // Tell the owner when this island is on screen (mounted as the active
    // section) so it can gate its autoplay clock — only advance while visible,
    // and resume rather than reset across the mount/unmount flicker on scroll.
    // Under reduced motion we never report visible, so the owner never autoplays.
    useEffect(() => {
        if (reduce) return;
        onVisible?.(true);
        return () => onVisible?.(false);
    }, [onVisible, reduce]);

    // When there are more slides than fits, show a windowed strip centred on the
    // active dot, shrinking the window-edge dots to imply more either side.
    const windowed = total > MAX_DOTS;
    const start = windowed
        ? Math.min(
              Math.max(activeIndex - Math.floor(MAX_DOTS / 2), 0),
              total - MAX_DOTS,
          )
        : 0;
    const count = windowed ? MAX_DOTS : total;
    const hasMoreBefore = windowed && start > 0;
    const hasMoreAfter = windowed && start + MAX_DOTS < total;

    return (
        <div className="flex items-center gap-3">
            {reduce ? null : (
                <button
                    type="button"
                    onClick={atEnd ? onReplay : onTogglePlay}
                    aria-label={
                        atEnd
                            ? 'Replay gallery'
                            : isPlaying
                              ? 'Pause gallery'
                              : 'Play gallery'
                    }
                    aria-pressed={atEnd ? undefined : isPlaying}
                    className={cn(
                        ISLAND_GLASS_LIGHT,
                        'flex size-14 items-center justify-center rounded-full transition-colors hover:bg-white/80',
                    )}
                >
                    {atEnd ? (
                        <ReplayIcon className="size-5" />
                    ) : isPlaying ? (
                        <PauseIcon className="size-5" />
                    ) : (
                        <PlayIcon className="ml-0.5 size-5" />
                    )}
                </button>
            )}
            <div
                role="group"
                aria-label={label}
                className={cn(
                    ISLAND_GLASS_LIGHT,
                    'flex h-14 items-center gap-2.5 rounded-full px-7',
                )}
            >
                {Array.from({ length: count }, (_, position) => {
                    const index = start + position;
                    const isActive = index === activeIndex;
                    const isEdge =
                        (position === 0 && hasMoreBefore) ||
                        (position === count - 1 && hasMoreAfter);
                    return (
                        <button
                            key={`dot-${index}`}
                            type="button"
                            onClick={() => onSelect(index)}
                            aria-label={`Go to slide ${index + 1}`}
                            aria-current={isActive}
                            className={cn(
                                'relative cursor-pointer overflow-hidden rounded-full transition-all duration-300',
                                isActive
                                    ? 'h-2 w-8 bg-gray-900/25'
                                    : isEdge
                                      ? 'size-1 bg-gray-500/40'
                                      : 'size-2 bg-gray-500/60 hover:bg-gray-700',
                            )}
                        >
                            {/* Active dot fills left-to-right over the autoplay
                                delay (Apple's progress indicator); freezes when
                                paused, restarts each slide. Reduced motion: a
                                static bar, no fill animation / auto-advance. */}
                            {isActive && reduce ? (
                                <span className="absolute inset-0 rounded-full bg-gray-900" />
                            ) : isActive ? (
                                <span
                                    className="absolute inset-0 origin-left animate-island-progress rounded-full bg-gray-900"
                                    style={{
                                        animationDuration: `${autoplayDelayMs}ms`,
                                        animationPlayState: isPlaying
                                            ? 'running'
                                            : 'paused',
                                    }}
                                    // The fill IS the autoplay clock — advancing
                                    // when it completes keeps pause/resume in sync
                                    // (no separate timer to drift out of phase).
                                    // Guard on the animation name so only the
                                    // progress fill completing advances, never a
                                    // stray animationend.
                                    onAnimationEnd={(event) => {
                                        // Visual-only when the owner drives
                                        // autoplay itself (e.g. the gallery's
                                        // timer); still advances for carousels
                                        // that opt into fill-driven advance.
                                        if (
                                            event.animationName ===
                                            'island-progress'
                                        )
                                            onAdvance?.();
                                    }}
                                />
                            ) : null}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
